import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Plane, 
  Calendar, 
  Clock, 
  Shield, 
  Car, 
  Phone, 
  MapPin,
  CheckCircle,
  Star,
  Briefcase,
  Users,
  Building,
  ArrowRight
} from "lucide-react";
import ServiceFAQ from "@/components/ServiceFAQ";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import randolphHeroImage from "@/assets/randolph-afb-hero.jpeg";

const RandolphAFBTransportation = () => {
  const faqs = [
    {
      question: "Can Rick's Black Car Service enter Randolph AFB?",
      answer: "Yes! Rick's Black Car Service has verified base access credentials (DBIDS) that allow our chauffeurs to enter Randolph AFB without a sponsor. We can drop you off and pick you up directly at your designated location on base."
    },
    {
      question: "What types of transportation do you provide to Randolph AFB?",
      answer: "We provide executive transportation for official business, VIP guest services, family visits, PCS relocations, airport transfers (SAT), and special events. Our luxury Audi fleet is perfect for both professional and personal transportation needs."
    },
    {
      question: "How far is Randolph AFB from San Antonio International Airport?",
      answer: "Randolph AFB is approximately 20 miles northeast of SAT Airport. Travel time is typically 28-35 minutes depending on traffic conditions. We monitor traffic in real-time to ensure on-time arrivals."
    },
    {
      question: "What information do you need for base entry?",
      answer: "We require the full legal name and date of birth for all passengers at booking. All passengers over 16 must have a valid government-issued photo ID (CAC, Driver's License, or Passport) ready to present at the gate."
    },
    {
      question: "Do you offer hourly service for meetings and official business?",
      answer: "Absolutely! Our hourly service ($100/hour, 3-hour minimum) is ideal for official visits with multiple stops, meetings, or tours. Your dedicated chauffeur stays with you throughout, ready whenever you need to move."
    },
    {
      question: "Can you accommodate visiting dignitaries or VIP guests?",
      answer: "Yes, we specialize in executive and VIP transportation. Our professional chauffeurs understand protocol and discretion. We can coordinate multiple vehicle convoys for larger delegations if needed."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Randolph AFB Transportation Service",
    "description": "Professional luxury transportation to Randolph Air Force Base, home of the iconic Taj Mahal administration building and T-38 Talon training aircraft. Executive travel, official business, VIP guests, and family transportation. Verified base access, DBIDS-credentialed chauffeurs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "Randolph Air Force Base, San Antonio, TX"
    },
    "serviceType": "Military Base Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ricksblackcarservicesantonio.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ricksblackcarservicesantonio.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Military Transportation",
        "item": "https://ricksblackcarservicesantonio.com/jbsa-military-transportation"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Randolph AFB Transportation",
        "item": "https://ricksblackcarservicesantonio.com/randolph-afb-transportation"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Randolph AFB Transportation | Executive Military Travel</title>
        <meta 
          name="description" 
          content="Professional Randolph AFB transportation. Verified base access, DBIDS-credentialed chauffeurs, luxury Audi fleet. Book today!" 
        />
        <meta name="keywords" content="Randolph AFB transportation, Randolph Air Force Base car service, executive military transportation San Antonio, JBSA Randolph, Taj Mahal building, T-38 Talon" />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/randolph-afb-transportation" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(${randolphHeroImage})`,
              backgroundPosition: 'center'
            }}
          />
          <div className="relative z-10 container mx-auto px-4 text-center">
            <Breadcrumb 
              items={[
                { name: "Services", url: "/services" },
                { name: "Military Transportation", url: "/services/military-transportation" },
                { name: "Randolph AFB", url: "/randolph-afb-transportation" }
              ]} 
            />
            <div className="flex justify-center mb-6">
              <div className="bg-primary/20 p-4 rounded-full">
                <Plane className="h-16 w-16 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Randolph AFB Transportation
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              Executive transportation for the "Showplace of the Air Force"
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Executive Transport</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg bg-background/10 border-white text-white hover:bg-white hover:text-foreground">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-card border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Shield className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Verified Base Access</span>
              </div>
              <div className="flex flex-col items-center">
                <CheckCircle className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">DBIDS Credentialed</span>
              </div>
              <div className="flex flex-col items-center">
                <Car className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Luxury Audi Fleet</span>
              </div>
              <div className="flex flex-col items-center">
                <Star className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">5-Star Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* About Randolph AFB */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">About Randolph AFB</h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  Known as the "Showplace of the Air Force," Randolph AFB is home to Air Education and Training Command (AETC) headquarters and numerous Air Force training and support organizations.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card className="border-primary/20">
                  <CardContent className="p-6 text-center">
                    <Building className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">AETC Headquarters</h3>
                    <p className="text-sm text-muted-foreground">
                      Home to Air Education and Training Command leadership
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20">
                  <CardContent className="p-6 text-center">
                    <Plane className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Pilot Training</h3>
                    <p className="text-sm text-muted-foreground">
                      12th Flying Training Wing and instructor pilot training
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-primary/20">
                  <CardContent className="p-6 text-center">
                    <Users className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Personnel Center</h3>
                    <p className="text-sm text-muted-foreground">
                      Air Force Personnel Center managing career assignments
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="text-center mt-8">
                <Link to="/blog/what-is-jbsa-access-guide" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                  Learn About JBSA Access Requirements
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Who We Serve at Randolph AFB</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Professional transportation for military personnel, officials, and families
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Briefcase className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Executive Officers</h3>
                  <p className="text-sm text-muted-foreground">
                    Discreet, professional transportation for senior leadership and staff
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">VIP Guests</h3>
                  <p className="text-sm text-muted-foreground">
                    Visiting dignitaries, government officials, and distinguished visitors
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Users className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Military Families</h3>
                  <p className="text-sm text-muted-foreground">
                    PCS moves, family visits, and special occasions
                  </p>
                </CardContent>
              </Card>
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <Building className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Contractors</h3>
                  <p className="text-sm text-muted-foreground">
                    Defense contractors and civilian employees with base business
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Transportation Packages */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Randolph AFB Transportation Packages</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Transparent pricing based on distance & time. 20% gratuity always included. No hidden fees.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Airport Package */}
              <Card className="relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-semibold">
                  MOST POPULAR
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    SAT Airport
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-2xl font-bold text-primary">~20 miles</span>
                    <p className="text-sm text-muted-foreground">to Randolph AFB</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flight monitoring
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Meet & greet service
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/quick-quote">Get Instant Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Downtown Package */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Downtown/Riverwalk
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-2xl font-bold text-primary">~18 miles</span>
                    <p className="text-sm text-muted-foreground">to Randolph AFB</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Hotel pickup available
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flexible timing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/quick-quote">Get Instant Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Hourly Package */}
              <Card className="border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Executive Hourly
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-4xl font-bold text-primary">$85</span>
                    <span className="text-muted-foreground">/hour</span>
                    <p className="text-sm text-muted-foreground">3-hour minimum</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Dedicated chauffeur
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Multiple stops included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Wait time included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild variant="outline" className="w-full">
                    <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Hourly Service</a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Pricing Disclaimer */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border/50 max-w-5xl mx-auto">
              <p className="text-sm text-muted-foreground text-center">
                <span className="font-medium text-foreground">Note:</span> Fares shown are estimates and calculated dynamically based on distance and time. 
                Actual fare may vary slightly depending on your exact pickup location. Use our{' '}
                <Link to="/request-quote" className="text-primary hover:underline font-medium">Instant Quote Calculator</Link>{' '}
                for a precise quote.
              </p>
            </div>
          </div>
        </section>

        {/* Location & Directions */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Location & Access</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-primary/20">
                  <CardHeader className="bg-primary/5">
                    <CardTitle className="flex items-center gap-3">
                      <MapPin className="h-6 w-6 text-primary" />
                      Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Address</p>
                        <p className="text-sm text-muted-foreground">1 Washington Circle, Universal City, TX 78148</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Car className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">From SAT Airport</p>
                        <p className="text-sm text-muted-foreground">~20 miles northeast, 25-35 min drive</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Building className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">From Downtown</p>
                        <p className="text-sm text-muted-foreground">~17 miles northeast, 20-30 min drive</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary/20">
                  <CardHeader className="bg-primary/5">
                    <CardTitle className="flex items-center gap-3">
                      <Shield className="h-6 w-6 text-primary" />
                      Base Entry
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Primary Gate</p>
                        <p className="text-sm text-muted-foreground">Main Gate on Pat Booker Road</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Our Access</p>
                        <p className="text-sm text-muted-foreground">DBIDS credentials allow unescorted commercial entry</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Your Requirement</p>
                        <p className="text-sm text-muted-foreground">Valid photo ID for all passengers over 16</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Important Information</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Shield className="h-5 w-5 text-primary" />
                      Required Documentation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>• Valid government-issued photo ID for all passengers over 16</p>
                    <p>• CAC (Common Access Card) if applicable</p>
                    <p>• Full legal name and DOB provided at booking</p>
                    <p>• Official orders or appointment documentation (recommended)</p>
                  </CardContent>
                </Card>

                <Card className="border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Calendar className="h-5 w-5 text-primary" />
                      Booking Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>• Book 24-48 hours in advance when possible</p>
                    <p>• VIP/executive bookings: 3+ days recommended</p>
                    <p>• Allow extra time for base security protocols</p>
                    <p>• Contact us for multi-day or recurring service rates</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <ServiceFAQ faqs={faqs} title="Randolph AFB Transportation FAQs" />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Book Executive Transport?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience professional, discreet transportation to Randolph AFB with verified base access and luxury service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Now</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default RandolphAFBTransportation;
